import React, {
    useCallback,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import "./Collapsible.scss";
import { isFunction } from "@common/helpers/typeGuards";
import { extractDataAttributes } from "./extractDataAttributes";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { CollapsibleHeader } from "./CollapsibleHeader";

interface CollapsibleProps {
    header: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode | ((opened: boolean) => React.ReactNode);
    footer?: React.ReactNode;
    defaultOpen?: boolean;
    className?: string;
    stacked?: boolean;
    open?: boolean;
    onToggle?: (opened: boolean) => void;
    first?: boolean;
    last?: boolean;
    middle?: boolean;
    collapseButtonSize?: TriggerButtonProps["size"];
}

/** @deprecated Use @design-system/Accordion/Accordion. Collapsible is deprecated. */
export const Collapsible: React.FC<CollapsibleProps> = extractDataAttributes(
    ({
        header,
        footer,
        className,
        defaultOpen,
        actions,
        children,
        stacked,
        open: forceOpen,
        onToggle,
        first,
        last,
        middle,
        dataAttributes,
        collapseButtonSize,
    }) => {
        const [open, setOpen] = useState(() => defaultOpen ?? false);
        const isOpened = forceOpen ?? open;
        const panelId = useId();
        const panelRef = useRef<HTMLDivElement>(null);

        useLayoutEffect(() => {
            if (panelRef.current) {
                panelRef.current.inert = !isOpened;
            }
        }, [isOpened]);

        const toggle = useCallback(() => {
            setOpen(!isOpened);
            onToggle?.(!isOpened);
        }, [isOpened, onToggle]);

        return (
            <section
                className={classNames("collapsible", className, {
                    "collapsible--open": isOpened,
                    "collapsible--stacked": stacked,
                    "collapsible--first": first,
                    "collapsible--last": last,
                    "collapsible--middle": middle,
                })}
                {...dataAttributes}
            >
                <CollapsibleHeader
                    isOpened={isOpened}
                    onToggle={toggle}
                    panelId={panelId}
                    actions={actions}
                    enabled={Boolean(children)}
                    collapseButtonSize={collapseButtonSize}
                >
                    {header}
                </CollapsibleHeader>

                <div
                    ref={panelRef}
                    id={panelId}
                    className="collapsible__collapse"
                >
                    <main className="collapsible__content">
                        {isFunction(children) ? children(isOpened) : children}
                        {footer ? (
                            <footer className="collapsible__footer">
                                {footer}
                            </footer>
                        ) : null}
                    </main>
                </div>
            </section>
        );
    },
);
